import React, { useEffect, useState } from "react";
import PageTitle from "@cs/components/src/PageTitle";
import { PageWrapperTwoColumn } from "@cs/components/src/PageWrapper";
import Button from "@cs/components/src/Button";
import Input from "@cs/components/src/Input";
import Section from "@cs/components/src/Section";
import Table from "@cs/components/src/Table";
import Spinner from "@cs/components/src/Spinner";
import KeyValue from "@cs/components/src/KeyValue";
import { Check, Plus, Trash2 } from "react-feather";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import ApiService from "../../services/ApiService";
import styled from "styled-components";

const EmailInputRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  column-gap: 10px;
  align-items: center;
  padding: 5px 10px 0;
`;

const isNew = (slackId: string | undefined) => !slackId || slackId === "new";

const UserEditScreen = () => {
  const { slackId } = useParams<{ slackId: string }>();
  const creating = isNew(slackId);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [slackIdInput, setSlackIdInput] = useState("");
  const [emails, setEmails] = useState<string[]>([]);
  const [newEmail, setNewEmail] = useState("");

  const { data, isLoading, isError } = useQuery({
    queryKey: ["user", slackId],
    enabled: !creating,
    queryFn: async (): Promise<AppUser> => {
      const response = await ApiService.fetch<{ data: AppUser }>(
        "GET",
        `/api/users/${slackId}`
      );
      return response.data;
    },
  });

  useEffect(() => {
    if (data) {
      setEmails(data.emails);
    }
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      await ApiService.fetch("POST", `/api/users`, {
        slackId: creating ? slackIdInput : slackId,
        emails,
      });
      queryClient.invalidateQueries({ queryKey: ["userList"] });
      navigate("/config/user");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (
        window.confirm(
          "Remove this user? They will no longer be able to log in."
        )
      ) {
        await ApiService.fetch("DELETE", `/api/users/${slackId}`);
        queryClient.invalidateQueries({ queryKey: ["userList"] });
        navigate("/config/user");
      }
    },
  });

  const addEmail = () => {
    const trimmed = newEmail.trim().toLowerCase();
    if (trimmed && !emails.includes(trimmed)) {
      setEmails([...emails, trimmed]);
    }
    setNewEmail("");
  };

  const removeEmail = (email: string) => {
    setEmails(emails.filter((e) => e !== email));
  };

  const effectiveSlackId = creating ? slackIdInput : slackId;
  const canSave = !!effectiveSlackId && emails.length > 0;

  return (
    <div>
      <PageTitle
        title={creating ? "Add User" : "Edit User"}
        previousRoute="/config/user"
        previousTitle="Users"
        error={
          isError
            ? "Problem loading user"
            : saveMutation.isError
            ? "Error saving user"
            : deleteMutation.isError
            ? "Error deleting user"
            : undefined
        }
      >
        {!creating && (
          <Button
            label="Delete User"
            icon={Trash2}
            onClick={() => deleteMutation.mutate()}
            loading={deleteMutation.isLoading}
            type="danger"
          />
        )}
        <Button
          label="Save"
          icon={Check}
          onClick={() => saveMutation.mutate()}
          loading={saveMutation.isLoading}
          type="success"
          disabled={!canSave}
        />
      </PageTitle>

      {isLoading ? (
        <Spinner />
      ) : (
        <PageWrapperTwoColumn>
          <Section title="User" pad>
            {creating ? (
              <Input
                label="Slack ID"
                value={slackIdInput}
                onChange={setSlackIdInput}
              />
            ) : (
              <KeyValue label="Slack ID" value={data?.slackId} />
            )}
          </Section>
          <Section title="Emails">
            <EmailInputRow>
              <Input
                label="Email address"
                value={newEmail}
                onChange={setNewEmail}
                onSubmit={addEmail}
              />
              <Button
                icon={Plus}
                onClick={addEmail}
                type="success"
                disabled={!newEmail.trim()}
              />
            </EmailInputRow>
            <Table
              columnGrid
              columns={[
                { title: "Email", width: "1fr" },
                { title: "", width: "auto" },
              ]}
              rows={emails.map((email) => ({
                id: email,
                element: () => (
                  <>
                    <div>{email}</div>
                    <Button
                      icon={Trash2}
                      onClick={() => removeEmail(email)}
                      type="danger"
                    />
                  </>
                ),
              }))}
              autoHeight={{ twoColumn: true }}
            />
          </Section>
        </PageWrapperTwoColumn>
      )}
    </div>
  );
};

export default UserEditScreen;
